import React, { useEffect, useRef } from 'react';

/**
 * Reusable Cloudflare Turnstile widget.
 * 
 * @param {Object} props
 * @param {function(string): void} props.onVerify - Callback triggered when Turnstile verification succeeds (passes token).
 * @param {function(): void} [props.onExpire] - Callback when verification token expires.
 * @param {function(): void} [props.onError] - Callback on Turnstile error.
 * @param {string} [props.action] - Optional Turnstile action label.
 * @param {'light'|'dark'|'auto'} [props.theme] - Visual theme.
 */
export default function Turnstile({ onVerify, onExpire, onError, action = 'generic', theme = 'light' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  
  // Keep callback refs stable across parent re-renders so Turnstile does not unmount/remount on keystrokes
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAFMku3C0Q1oHDBDi';

  useEffect(() => {
    let active = true;
    let observer = null;

    const loadAndInit = () => {
      // Load Turnstile global script dynamically if not present
      if (!document.getElementById('cloudflare-turnstile-script')) {
        const script = document.createElement('script');
        script.id = 'cloudflare-turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }

      const renderWidget = () => {
        if (!active || !containerRef.current || !window.turnstile) return;

        try {
          if (widgetIdRef.current !== null) {
            return;
          }

          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            theme: theme,
            action: action,
            callback: (token) => {
              if (active && onVerifyRef.current) {
                onVerifyRef.current(token);
              }
            },
            'expired-callback': () => {
              if (active && onExpireRef.current) {
                onExpireRef.current();
              }
            },
            'error-callback': (code) => {
              console.warn('Turnstile verification error code:', code);
              if (active && onErrorRef.current) {
                onErrorRef.current(code);
              }
            },
          });
        } catch (err) {
          console.error('Failed to render Turnstile widget:', err);
        }
      };

      const checkAndInit = () => {
        if (!active) return;
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          renderWidget();
        } else {
          setTimeout(checkAndInit, 100);
        }
      };

      checkAndInit();
    };

    if ('IntersectionObserver' in window && containerRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadAndInit();
          if (observer) {
            observer.disconnect();
          }
        }
      }, { rootMargin: '300px' });
      observer.observe(containerRef.current);
    } else {
      loadAndInit();
    }

    return () => {
      active = false;
      if (observer) {
        observer.disconnect();
      }
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignore unmount error
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, action]);

  return (
    <div 
      ref={containerRef} 
      className="inline-block my-2" 
      style={{ minHeight: '65px' }}
    />
  );
}
